import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Edit, CreditCard } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

// Componentes UI Globales
import { FormError } from '../components/ui/FormError'
import { SearchFilter } from '../components/ui/SearchFilter'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { Input } from '../components/ui/input'
import { TableSkeleton } from '../components/ui/table-skeleton'
// Servicios
import {
  paymentMethodService,
  PaymentMethodItem,
} from '../services/payment-method.service'

// ─── 1. Esquema de Validación (Zod) ──────────────────────────────────────────
const formSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(255, 'Demasiado largo'),
  code: z
    .string()
    .min(1, 'El código es obligatorio')
    .max(50, 'Máximo 50 caracteres')
    .regex(/^[a-z0-9_]+$/, 'Solo minúsculas, números y guiones bajos (_)'),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().min(0, 'El orden no puede ser negativo').max(65535),
  requires_reference: z.boolean().default(false),
  allows_change: z.boolean().default(false),
})

type FormValues = {
  name: string
  code: string
  is_active: boolean
  sort_order: number
  requires_reference: boolean
  allows_change: boolean
}

type PaginationMeta = {
  total: number
  from: number | null
  to: number | null
}

// ─── 2. Componente Principal ───────────────────────────────────────────────
export function PaymentMethods() {
  // ─── Estados ───
  const [data, setData] = useState<PaymentMethodItem[]>([])
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
    total: 0,
    from: 0,
    to: 0,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshCount, setRefreshCount] = useState(0)

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // ─── Formulario (React Hook Form) ───
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      code: '',
      is_active: true,
      sort_order: 0,
      requires_reference: false,
      allows_change: false,
    },
  })

  const isActiveValue = watch('is_active')
  const requiresReferenceValue = watch('requires_reference')
  const allowsChangeValue = watch('allows_change')

  // ─── Cargar Datos de la API ───
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await paymentMethodService.getItems({
          search: searchQuery,
          page: currentPage,
          per_page: itemsPerPage,
        })

        const items = response.data?.data || response.data || []
        setData(items)
        setPaginationMeta(response.data || response.meta || { total: 0, from: 0, to: 0 })
      } catch {
        toast.error('Error al cargar los métodos de pago')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [refreshCount, searchQuery, currentPage])

  const paginatedData = data

  // ─── Funciones de Interfaz ───
  const openCreateModal = () => {
    setEditingId(null)
    reset({
      name: '',
      code: '',
      is_active: true,
      sort_order: 0,
      requires_reference: false,
      allows_change: false,
    })
    setShowModal(true)
  }

  const openEditModal = (item: PaymentMethodItem) => {
    setEditingId(item.id)
    reset({
      name: item.name,
      code: item.code,
      is_active: item.is_active,
      sort_order: item.sort_order,
      requires_reference: item.requires_reference,
      allows_change: item.allows_change,
    })
    setShowModal(true)
  }

  // ─── Funciones API ───
  const onSubmit = async (formData: FormValues) => {
    try {
      if (editingId) {
        await paymentMethodService.updateItem(editingId, {
          name: formData.name,
          is_active: formData.is_active,
          sort_order: formData.sort_order,
          requires_reference: formData.requires_reference,
          allows_change: formData.allows_change,
        })
        toast.success('Método de pago actualizado')
      } else {
        await paymentMethodService.createItem(formData)
        toast.success('Método de pago creado')
      }
      setShowModal(false)
      setRefreshCount((prev) => prev + 1)
    } catch {
      toast.error('Error al guardar el método de pago')
    }
  }

  // ─── Renderizado (JSX) ───────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6">
      {/* 1. Header y Buscador */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Métodos de Pago</h1>
          <p className="mt-1 text-sm text-slate-600">
            Gestiona las formas de pago aceptadas en el punto de venta.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-64">
            <SearchFilter
              placeholder="Buscar..."
              onSearch={(text: string) => {
                setSearchQuery(text)
                setCurrentPage(1)
              }}
            />
          </div>
          {/* Ocultamos la creación por ahora
          <Button onClick={openCreateModal} className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" /> Nuevo Método
          </Button>
          */}
        </div>
      </div>

      {/* 2. Tabla de Datos */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Código', 'Orden', 'Referencia', 'Cambio', 'Estado', 'Acciones']}
          columns={['text', 'text', 'text', 'badge', 'badge', 'badge', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Orden
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Referencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Cambio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {item.code}
                      </td>
                      <td className="px-6 py-4 text-slate-700">{item.sort_order}</td>
                      <td className="px-6 py-4">
                        <Badge className={item.requires_reference ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                          {item.requires_reference ? 'Sí' : 'No'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={item.allows_change ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                          {item.allows_change ? 'Sí' : 'No'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={
                            item.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }
                        >
                          {item.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(item)}
                            className="rounded p-1 hover:bg-slate-100 transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4 text-slate-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <CreditCard className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron métodos de pago</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 3. Paginación */}
          {paginationMeta.total > 0 && (
            <GlobalPaginator
              totalItems={paginationMeta.total}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              from={paginationMeta.from}
              to={paginationMeta.to}
            />
          )}
        </div>
      )}

      {/* 4. Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Editar Método de Pago' : 'Nuevo Método de Pago'}
            </DialogTitle>
            <DialogDescription>
              Configura cómo se presenta y se captura este método durante una venta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
            {/* Ocultamos la edición del código por regla de negocio
            <div>
              <label htmlFor="code" className="mb-1 block text-sm font-medium text-slate-700">
                Código *
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Código <span className="text-red-500">*</span>
              </label>
              <Input
                id="code"
                {...register('code')}
                disabled={editingId !== null}
                placeholder="ej. efectivo, tarjeta_credito"
                className="font-mono text-sm bg-slate-100 text-slate-500 cursor-not-allowed"
                disabled
              />
              {errors.code && <FormError message={errors.code.message} />}
              <p className="mt-1 text-xs text-slate-500">
                Identificador único interno (solo minúsculas y guiones bajos).
              </p>
            </div>
            */}
            <input type="hidden" {...register('code')} />

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Nombre <span className="text-red-500">*</span>
              </label>
              <Input id="name" {...register('name')} placeholder="Efectivo, Tarjeta de Crédito" />
              {errors.name && <FormError message={errors.name.message} />}
            </div>

            <div>
              <label htmlFor="sort_order" className="mb-1 block text-sm font-medium text-slate-700">
                Orden de aparición
              </label>
              <Input
                id="sort_order"
                type="number"
                min={0}
                max={65535}
                {...register('sort_order', { valueAsNumber: true })}
              />
              {errors.sort_order && <FormError message={errors.sort_order.message} />}
              <p className="mt-1 text-xs text-slate-500">
                Los números menores aparecen primero.
              </p>
            </div>

            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="requires_reference"
                  checked={requiresReferenceValue}
                  onCheckedChange={(value) =>
                    setValue('requires_reference', value === true, { shouldValidate: true })
                  }
                />
                <div>
                  <label htmlFor="requires_reference" className="text-sm font-medium text-slate-700">
                    Solicita referencia
                  </label>
                  <p className="text-xs text-slate-500">
                    Requiere capturar una autorización, folio o referencia del pago.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="allows_change"
                  checked={allowsChangeValue}
                  onCheckedChange={(value) =>
                    setValue('allows_change', value === true, { shouldValidate: true })
                  }
                />
                <div>
                  <label htmlFor="allows_change" className="text-sm font-medium text-slate-700">
                    Permite cambio
                  </label>
                  <p className="text-xs text-slate-500">
                    Permite recibir un importe mayor y devolver la diferencia.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <Checkbox
                id="is_active"
                checked={isActiveValue}
                onCheckedChange={(value) =>
                  setValue('is_active', value === true, { shouldValidate: true })
                }
              />
              <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                Estado Activo
              </label>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
